export type BudgetTaxAmountType = 'Percent' | 'Fixed';

export interface BudgetTaxSummaryInputTax {
  id?: number | null;
  name?: string | null;
  amount?: number | null;
  amountType?: string | null;
}

export interface BudgetTaxSummaryInputRelation {
  taxId?: number | null;
  tax?: BudgetTaxSummaryInputTax | null;
}

export interface BudgetTaxSummaryInputLine {
  quantity?: number | null;
  price?: number | null;
  priceSubtotal?: number | null;
  discount?: number | null;
  /** Impuesto guardado por el servidor para la línea, ya en la divisa del presupuesto. */
  taxAmount?: number | null;
  /** Importe con impuestos de la línea: en el PDF, el que imprime la columna «Importe». */
  priceTotal?: number | null;
  taxes?: BudgetTaxSummaryInputRelation[] | null;
}

export interface BudgetTaxSummaryRow {
  key: string;
  taxId: number;
  amountType: BudgetTaxAmountType;
  label: string;
  amount: number;
}

export interface BudgetTaxSummary {
  rows: BudgetTaxSummaryRow[];
  totalTaxAmount: number;
}

interface TaxGroup {
  key: string;
  taxId: number;
  amountType: BudgetTaxAmountType;
  name: string;
  rate: number;
  amount: number;
}

/**
 * Resumen fiscal del pie del PDF.
 *
 * `totalTaxAmount` —la cifra que se imprime como «Impuesto»— SUMA el impuesto guardado en cada línea
 * (CEC-669 / CEC-666, decisión 5 de CEC-565: el documento cuadra con lo guardado aunque se redondee
 * dos veces). Recalcular el porcentaje sobre el subtotal hacía que el pie no coincidiera con la
 * cotización ni con su propia columna «Importe»: arrastraba decimales que la línea ya había
 * redondeado (S000317, CLP) y cobraba dos veces una línea con dos relaciones del mismo impuesto
 * (S000090, S000033). Medido en PRE el 15/09/2026: recalculando descuadraban 23 cotizaciones;
 * sumando lo guardado, ninguna.
 *
 * `rows` sigue describiendo las RELACIONES de cada línea con su contribución teórica: el documento
 * no las imprime, y con datos sucios su suma puede no coincidir con `totalTaxAmount`, que manda.
 */
export function buildBudgetTaxSummary(lines: BudgetTaxSummaryInputLine[]): BudgetTaxSummary {
  const groups = new Map<string, TaxGroup>();
  let totalTaxAmount = 0;

  for (const line of lines) {
    const storedTax = resolveStoredLineTax(line);
    let computedLineTax = 0;

    for (const relation of line.taxes ?? []) {
      if (!relation.tax) continue;

      const taxId = resolveTaxId(relation);
      const amountType = normalizeAmountType(relation.tax.amountType);
      const key = `${taxId}:${amountType}`;
      const name = (relation.tax.name ?? '').trim();
      const rate = requireFinite(relation.tax.amount, `La identidad fiscal ${key} contiene un importe inválido.`);
      if (!name) {
        throw new Error(`La identidad fiscal ${key} no contiene un nombre.`);
      }

      const contribution = calculateTaxContribution(line, rate, amountType);
      computedLineTax += contribution;

      const existing = groups.get(key);
      if (existing) {
        if (existing.name !== name || existing.rate !== rate) {
          throw new Error(`La identidad fiscal ${key} contiene metadata incompatible.`);
        }
        existing.amount += contribution;
      } else {
        groups.set(key, { key, taxId, amountType, name, rate, amount: contribution });
      }
    }

    totalTaxAmount += storedTax ?? computedLineTax;
  }

  const orderedGroups = Array.from(groups.values()).sort((left, right) =>
    left.taxId - right.taxId || left.amountType.localeCompare(right.amountType)
  );
  const hasPositiveContribution = orderedGroups.some(group => group.amount > 0);
  const visibleGroups = orderedGroups.filter(group =>
    !(hasPositiveContribution
      && group.amountType === 'Percent'
      && group.rate === 0
      && group.amount === 0)
  );

  return {
    rows: visibleGroups.map(group => ({
      key: group.key,
      taxId: group.taxId,
      amountType: group.amountType,
      label: group.amountType === 'Percent'
        ? `${group.name} ${group.rate}%`
        : group.name,
      amount: group.amount,
    })),
    totalTaxAmount,
  };
}

/**
 * El impuesto que el servidor guardó para la línea, o `null` si no se puede saber.
 *
 * 1. `taxAmount`, tal cual: un 0 guardado es un 0, no un dato ausente.
 * 2. Si falta, `priceTotal − priceSubtotal`: lo que la propia fila imprime como Importe menos su
 *    neto, así que el pie sigue cuadrando con la columna. En PRE (1.945 líneas, 15/09/2026)
 *    coincide con `taxAmount` en todas.
 * 3. Si tampoco hay importes guardados (líneas montadas en local), `null`: el llamador conserva el
 *    cálculo por relaciones.
 */
export function resolveStoredLineTax(line: BudgetTaxSummaryInputLine): number | null {
  const taxAmount = toFiniteOrNull(line.taxAmount);
  if (taxAmount !== null) {
    return taxAmount;
  }

  const priceTotal = toFiniteOrNull(line.priceTotal);
  const priceSubtotal = toFiniteOrNull(line.priceSubtotal);
  return priceTotal !== null && priceSubtotal !== null ? priceTotal - priceSubtotal : null;
}

/** La API entrega importes como número, como texto o envueltos en `{ parsedValue }`. */
function toFiniteOrNull(value: unknown): number | null {
  const raw = typeof value === 'object' && value !== null && 'parsedValue' in value
    ? (value as { parsedValue: unknown }).parsedValue
    : value;
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function calculateTaxContribution(
  line: BudgetTaxSummaryInputLine,
  taxAmount: number,
  amountType: BudgetTaxAmountType,
): number {
  const quantity = requireFinite(line.quantity ?? 0, 'La línea contiene una cantidad fiscal inválida.');
  if (amountType === 'Fixed') {
    return taxAmount * quantity;
  }

  // `priceSubtotal` llega del backend YA descontado; `precio × cantidad` es bruto.
  // El descuento solo se aplica en el segundo caso (CEC-586, verificado en S814476).
  const price = requireFinite(line.price ?? 0, 'La línea contiene un precio fiscal inválido.');
  const discount = requireFinite(line.discount ?? 0, 'La línea contiene un descuento fiscal inválido.');
  if (line.priceSubtotal === undefined || line.priceSubtotal === null) {
    const gross = price * quantity;
    return (gross - (gross * discount / 100)) * taxAmount / 100;
  }

  const net = requireFinite(line.priceSubtotal, 'La línea contiene un subtotal fiscal inválido.');
  return net * taxAmount / 100;
}

function resolveTaxId(relation: BudgetTaxSummaryInputRelation): number {
  const relationId = positiveIntegerOrNull(relation.taxId);
  const taxId = positiveIntegerOrNull(relation.tax?.id);
  if (relationId !== null && taxId !== null && relationId !== taxId) {
    throw new Error('La relación fiscal contiene identidades incompatibles.');
  }

  const resolved = relationId ?? taxId;
  if (resolved === null) {
    throw new Error('La relación fiscal no contiene una identidad positiva.');
  }
  return resolved;
}

function normalizeAmountType(value: string | null | undefined): BudgetTaxAmountType {
  const normalized = (value ?? 'Percent').trim().toLowerCase();
  if (normalized === 'percent') return 'Percent';
  if (normalized === 'fixed') return 'Fixed';
  throw new Error(`El tipo fiscal "${value}" no está soportado.`);
}

function positiveIntegerOrNull(value: number | null | undefined): number | null {
  return Number.isInteger(value) && Number(value) > 0 ? Number(value) : null;
}

function requireFinite(value: number | null | undefined, message: string): number {
  if (!Number.isFinite(value)) throw new Error(message);
  return Number(value);
}
